import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import MessageBox from './MessageBox';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [username, setUsername] = useState('');
  const [stats, setStats] = useState({
    totalLostItems: 0,
    totalFoundItems: 0,
    pendingClaims: 0,
    recentActivity: []
  });

  const showMessageBox = (msg, type = 'info') => {
    setMessage(msg);
    setMessageType(type);
  };

  const closeMessageBox = () => {
    setMessage('');
    setMessageType('info');
  };

  useEffect(() => {
    const checkAdminAndLoadData = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        // Get username from localStorage
        const storedUsername = localStorage.getItem("username");
        setUsername(storedUsername || user.email);

        // Check if user is admin
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists() || userDoc.data().role !== 'admin') {
          showMessageBox('Access denied. Admin privileges required.', 'danger');
          setTimeout(() => navigate('/homepage'), 2000);
          return;
        }

        setIsAdmin(true);

        // Load dashboard stats
        const lostItemsQuery = query(collection(db, 'items'), where('type', '==', 'lost'));
        const foundItemsQuery = query(collection(db, 'items'), where('type', '==', 'found'));
        const pendingClaimsQuery = query(
          collection(db, 'items'), 
          where('claimRequest.status', '==', 'pending')
        );

        const [lostSnapshot, foundSnapshot, pendingSnapshot] = await Promise.all([
          getDocs(lostItemsQuery),
          getDocs(foundItemsQuery),
          getDocs(pendingClaimsQuery)
        ]);

        setStats({
          totalLostItems: lostSnapshot.size,
          totalFoundItems: foundSnapshot.size,
          pendingClaims: pendingSnapshot.size,
          recentActivity: []
        });

      } catch (error) {
        console.error('Error loading admin dashboard:', error);
        showMessageBox('Error loading admin dashboard: ' + error.message, 'danger');
      } finally {
        setLoading(false);
      }
    };

    checkAdminAndLoadData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <>
        {/* Navbar */}
        <nav className="flex items-center bg-white border-b px-6 py-3 shadow sticky top-0 z-40">
          <Link className="mr-2" to="/homepage">
            <img
              src="/Topbar-christ.jpg"
              alt="Christ University Logo"
              className="logo-christ max-w-[150px] h-auto"
            />
          </Link>
        </nav>

        <div className="container mx-auto my-20 px-6 text-center">
          {message && <MessageBox message={message} type={messageType} onClose={closeMessageBox} />}
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md mx-auto">
            <div className="text-red-600 text-6xl mb-4">
              <i className="bi bi-shield-exclamation"></i>
            </div>
            <h3 className="text-red-600 font-bold text-2xl mb-4">Access Denied</h3>
            <p className="text-gray-600 mb-6">You don't have permission to access the admin panel.</p>
            <Link 
              to="/homepage" 
              className="bg-blue-600 text-white px-6 py-3 rounded-full font-bold hover:bg-blue-700 transition"
            >
              Go to Homepage
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Navbar */}
      <nav className="flex items-center bg-white border-b px-6 py-3 shadow sticky top-0 z-40">
        <Link className="mr-2" to="/homepage">
          <img
            src="/Topbar-christ.jpg"
            alt="Christ University Logo"
            className="logo-christ max-w-[150px] h-auto"
          />
        </Link>
        <div className="ml-auto flex items-center gap-3">
          <span className="font-bold text-blue-900">
            Welcome: {username}
            <span className="ml-2 px-2 py-1 rounded-lg bg-yellow-200 text-yellow-900 text-xs font-semibold">Admin</span>
          </span>
          <button
            onClick={handleLogout}
            type="button"
            className="bg-white text-center w-48 rounded-2xl h-14 relative text-black text-xl font-semibold group"
          >
            <div className="bg-red-400 rounded-xl h-12 w-1/4 flex items-center justify-center absolute left-1 top-[4px] group-hover:w-[184px] z-10 duration-500">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" height="25px" width="25px">
                <path d="M224 480h640a32 32 0 1 1 0 64H224a32 32 0 0 1 0-64z" fill="#000000"></path>
                <path d="m237.248 512 265.408 265.344a32 32 0 0 1-45.312 45.312l-288-288a32 32 0 0 1 0-45.312l288-288a32 32 0 1 1 45.312 45.312L237.248 512z" fill="#000000"></path>
              </svg>
            </div>
            <p className="translate-x-2">LOGOUT</p>
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto my-8 px-6">
        {message && <MessageBox message={message} type={messageType} onClose={closeMessageBox} />}
        
        <div className="text-center mb-8">
          <h2 className="font-bold text-blue-900 text-3xl mb-2">
            <i className="bi bi-gear-fill me-2"></i>
            Admin Dashboard
          </h2>
          <p className="text-gray-600">Manage and monitor the Lost & Found system</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg border border-red-200 p-6 text-center">
            <div className="text-red-600 text-4xl mb-4">
              <i className="bi bi-exclamation-triangle"></i>
            </div>
            <h3 className="text-red-600 text-3xl font-bold">{stats.totalLostItems}</h3>
            <p className="text-gray-600 mb-4">Total Lost Items</p>
            <Link 
              to="/all-lost-items" 
              className="bg-red-100 text-red-700 px-4 py-2 rounded-full font-semibold hover:bg-red-200 transition"
            >
              View All Lost
            </Link>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg border border-green-200 p-6 text-center">
            <div className="text-green-600 text-4xl mb-4">
              <i className="bi bi-check-circle"></i>
            </div>
            <h3 className="text-green-600 text-3xl font-bold">{stats.totalFoundItems}</h3>
            <p className="text-gray-600 mb-4">Total Found Items</p>
            <Link 
              to="/all-found-items" 
              className="bg-green-100 text-green-700 px-4 py-2 rounded-full font-semibold hover:bg-green-200 transition"
            >
              View All Found
            </Link>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg border border-yellow-200 p-6 text-center">
            <div className="text-yellow-600 text-4xl mb-4">
              <i className="bi bi-clock"></i>
            </div>
            <h3 className="text-yellow-600 text-3xl font-bold">{stats.pendingClaims}</h3>
            <p className="text-gray-600 mb-4">Pending Claims</p>
            <Link 
              to="/history" 
              className="bg-yellow-100 text-yellow-700 px-4 py-2 rounded-full font-semibold hover:bg-yellow-200 transition"
            >
              Review Claims
            </Link>
          </div>
        </div>

        {/* Admin Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h5 className="font-bold text-xl mb-4 text-blue-900">
              <i className="bi bi-list-ul me-2"></i>Quick Actions
            </h5>
            <div className="space-y-3">
              <Link 
                to="/all-lost-items" 
                className="block w-full bg-red-100 text-red-700 px-4 py-3 rounded-lg font-semibold hover:bg-red-200 transition text-center"
              >
                <i className="bi bi-exclamation-triangle me-2"></i>
                Manage Lost Items
              </Link>
              <Link 
                to="/all-found-items" 
                className="block w-full bg-green-100 text-green-700 px-4 py-3 rounded-lg font-semibold hover:bg-green-200 transition text-center"
              >
                <i className="bi bi-check-circle me-2"></i>
                Manage Found Items
              </Link>
              <Link 
                to="/history" 
                className="block w-full bg-blue-100 text-blue-700 px-4 py-3 rounded-lg font-semibold hover:bg-blue-200 transition text-center"
              >
                <i className="bi bi-clock-history me-2"></i>
                View Claims History
              </Link>
              <Link 
                to="/reportitem" 
                className="block w-full bg-purple-100 text-purple-700 px-4 py-3 rounded-lg font-semibold hover:bg-purple-200 transition text-center"
              >
                <i className="bi bi-plus-circle me-2"></i>
                Report New Item
              </Link>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h5 className="font-bold text-xl mb-4 text-blue-900">
              <i className="bi bi-bar-chart me-2"></i>System Overview
            </h5>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="font-medium text-gray-700">System Status:</span>
                <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-semibold">Online</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="font-medium text-gray-700">Database:</span>
                <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-semibold">Connected</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="font-medium text-gray-700">Recent Activity:</span>
                <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold">{stats.recentActivity.length} events</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="font-medium text-gray-700">Admin Level:</span>
                <span className="px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 text-sm font-semibold">Full Access</span>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="text-center mt-8">
          <Link 
            to="/homepage" 
            className="bg-gray-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-gray-700 transition mr-4"
          >
            <i className="bi bi-house me-2"></i>
            Back to Home
          </Link>
          <Link 
            to="/reportitem" 
            className="bg-blue-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-700 transition"
          >
            <i className="bi bi-plus-circle me-2"></i>
            Report Item
          </Link>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
